/* ============================================
   useCachedData — el caché para páginas que no son "una tabla"

   `useCachedQuery` resuelve el caso simple: una página = una tabla del CRUD.
   La mayoría de las pantallas no es eso — Proyectos trae cinco listas de una
   vez, el Resumen del portal encadena proyectos → tareas → apps → consumo, y
   varias piden la misma ruta con filtros en la URL. Para esas, el `queryFn`
   tiene que ser la función de carga que la página YA tiene, no una tabla.

   De ahí la forma de este hook: recibe la clave y el cargador tal cual, así
   migrar una pantalla es envolver su `loadData` en vez de reescribirla. Lo que
   aporta es solo CUÁNDO se pide: React Query guarda el resultado y lo conserva
   al cambiar de ruta, así que volver a la pestaña no vuelve a pedir nada.
   React Router desmonta la página al navegar, y con `useState` + `useEffect`
   eso significaba perder los datos y pedirlos de cero en cada visita.

   Los cinco minutos de `staleTime` son el trato: dentro de esa ventana no
   pregunta al servidor. Pasada, al volver a entrar muestra lo que tenía y
   revalida detrás — nunca un "Cargando" en blanco sobre datos que ya existen.
   Una pantalla que necesite el dato al instante (un chat, una cola que alguien
   más mueve) pasa su propio `staleTime` o llama a `refrescar()`.
   ============================================ */

import { useQuery, useQueryClient } from '@tanstack/react-query'

export const CINCO_MIN = 5 * 60 * 1000

type Opciones = {
  /** `false` deja la consulta en espera (falta el id, falta la sesión). */
  activo?: boolean
  /** Cuánto vale el dato antes de revalidar. Por defecto 5 min. */
  staleTime?: number
}

export function useCachedData<T>(clave: unknown[], cargar: () => Promise<T>, opciones?: Opciones) {
  const qc = useQueryClient()
  const q = useQuery<T>({
    queryKey: clave,
    queryFn: cargar,
    enabled: opciones?.activo ?? true,
    staleTime: opciones?.staleTime ?? CINCO_MIN,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  return {
    /**
     * Retoca lo que ya está en caché, con la misma forma `prev => next` que
     * tenía el `setState` de antes. Es lo que mantiene vivos los cambios
     * optimistas: sin esto, subir un comprobante tendría que volver a pedir la
     * lista entera para que la fila se actualice en pantalla.
     */
    actualizar: (fn: (prev: T) => T) => qc.setQueryData<T>(clave, prev => (prev === undefined ? prev : fn(prev))),
    data: q.data,
    /** Solo la PRIMERA carga. Una revalidación con datos en mano no es "cargando". */
    isLoading: q.isLoading,
    /** Hay una petición en vuelo, con o sin datos previos. Para un spinner discreto. */
    isFetching: q.isFetching,
    error: q.error,
    refrescar: q.refetch,
  }
}

/**
 * Invalidar a mano después de escribir. Las páginas migradas guardan con el
 * `api.post/put/del` que ya tenían, y el caché no se enteraría: tras guardar
 * hay que avisarle cuál clave quedó vieja, o la pantalla seguiría mostrando lo
 * de antes hasta que se cumplan los cinco minutos.
 */
export function useInvalidar() {
  const qc = useQueryClient()
  return (clave: unknown[]) => qc.invalidateQueries({ queryKey: clave })
}
